import { memo } from "react";

import styled from "styled-components";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";

import { useFindingStore, Finding } from "stores/FindingStore";

import BulkActions from "./BulkActions";
import Filters from "./Filters";
import { SeverityEntry, SourceEntry, StateEntry } from "./Filters/data";

// -----------------------------------------------------------------------------

const OrganizationFindingsHeader = memo(function OrganizationFindingsHeader({
  selectedFindings,
  resetSelectedFindings,
  severities,
  setSeverities,
  states,
  setStates,
  sources,
  setSources,
  sorting,
  setSorting,
  sortDirection,
  setSortDirection,
  searchText,
  setSearchText,
  showFirstConnectionWarning,
}: Props): JSX.Element {
  const [findingsStore] = useFindingStore();
  const findingsCount = findingsStore.findingsCount ?? 0;
  return (
    <Box sx={{ backgroundColor: "#FFF", p: 3, overflow: "auto" }}>
      {showFirstConnectionWarning ? (
        <AlertStyled
          variant="filled"
          severity="warning"
          sx={{ mb: 2 }}
          icon={false}
        >
          <Loader>
            <CircularProgress size={16} color="inherit" />
          </Loader>
          <span>
            ThreatKey is hard at work looking for potential issues with your
            most recent connection. Please wait a few minutes before refreshing.
          </span>
        </AlertStyled>
      ) : null}

      <Typography variant="h4" fontWeight="bold" mb={2} display="inline">
        Overview of{" "}
      </Typography>
      <Typography variant="h4" fontWeight="bold" display="inline">
        {findingsCount} Finding{findingsCount !== 1 && "s"}
      </Typography>
      <Stack spacing={1} mt={2} direction="row">
        <TextField
          size="small"
          placeholder="Search"
          sx={{ flex: 1, minWidth: 96 }}
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchText ? (
              <InputAdornment position="end">
                <CancelIcon
                  style={{ cursor: "pointer" }}
                  onClick={() => setSearchText("")}
                />
              </InputAdornment>
            ) : null,
          }}
        />

        {selectedFindings.length > 1 ? (
          <BulkActions
            findings={selectedFindings}
            resetSelectedFindings={resetSelectedFindings}
          />
        ) : (
          <Filters
            severities={severities}
            setSeverities={setSeverities}
            states={states}
            setStates={setStates}
            sources={sources}
            setSources={setSources}
            sorting={sorting}
            setSorting={setSorting}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
          />
        )}
      </Stack>
    </Box>
  );
});

export default OrganizationFindingsHeader;

// -----------------------------------------------------------------------------

const AlertStyled = styled(Alert)`
  // I'm not going to add an entire layer of MUI theming to change
  // the color of one alert once
  background: #afa !important;
  border: 0 !important;
  color: #000;

  .MuiAlert-message {
    display: grid;
    gap: 8px;
    grid-template-columns: auto 1fr;
  }
`;

const Loader = styled.div`
  align-items: center;
  display: flex;
`;

// -----------------------------------------------------------------------------

interface Props {
  selectedFindings: Finding[];
  resetSelectedFindings: (
    finding: Finding,
    appendToList: boolean,
    removeFromList: boolean
  ) => void;
  severities: SeverityEntry[];
  setSeverities: (severities: SeverityEntry[]) => void;
  states: StateEntry[];
  setStates: (states: StateEntry[]) => void;
  sources: SourceEntry[];
  setSources: (sources: SourceEntry[]) => void;
  sorting: string;
  setSorting: (sort: string) => void;
  sortDirection: number;
  setSortDirection: (direction: number) => void;
  searchText: string;
  setSearchText: (searchText: string) => void;
  showFirstConnectionWarning: boolean;
}
